import {
  get,
  ClassifierResponseBody,
  ClassifierRequestBody,
  ClassifierRequestType,
  GroupedSubtypeClassifierResponseBody,
  post,
} from "../api/fetch";
import { VaraOsisType } from "../types/proceedings/ProceedingAssetDetailTypes";
import { AssetType } from "../types/reports/ReportTypes";
import { Unspecified } from "./conversionUtils";
import { getStoredClassifierSets, getOrCreateStoredClassifierSets, storeClassifiers } from "./sessionStorageManager";
import { AddressSearchType, RealEstateComplexAddressSearchType } from "./addressComponent/AddressComponentTypes";
import { ProcessingStatus } from "../types/contracts/workspaces/ContractWorkspaceSearchTypes";

const getClassifiers = async (
  requestedTypes: ClassifierType[],
  languageCode: LanguageCode = "ET",
): Promise<ClassifierSet[]> => {
  const storedClassifierSets = retrieveClassifierSets(requestedTypes, languageCode);
  if (storedClassifierSets.length) {
    storedClassifierSets.forEach((storedClassifierSet) => {
      requestedTypes = requestedTypes.filter((type) => !(storedClassifierSet.type === type));
    });
  }

  let fetchedClassifierSets: ClassifierSet[] = [];
  if (requestedTypes.length) {
    const requestTypes = requestedTypes.map((type) => {
      return ClassifierTypeMap.get(type)!;
    });
    await fetchClassifierSets(requestTypes, languageCode).then((classifiers) => {
      storeClassifierSets(classifiers, languageCode);
      fetchedClassifierSets = classifiers;
    });
  }
  return storedClassifierSets.concat(fetchedClassifierSets);
};

const getGroupedSubtypeClassifiers = async (
  start: string = "false",
  languageCode: LanguageCode = "ET",
): Promise<Map<string, Classifier[]>> => {
  /*const storedClassifierSets = retrieveClassifierSets(
		languageCode
	);*/
  let fetchedClassifierSets: Map<string, Classifier[]> = new Map();
  await fetchGroupedSubtypeClassifiers(start, languageCode).then((classifiers) => {
    //storeClassifierSets(classifiers, languageCode);
    fetchedClassifierSets = classifiers;
  });
  return fetchedClassifierSets;
};

const getContractGroupedSubtypeClassifiers = async (
  languageCode: LanguageCode = "ET",
): Promise<Map<string, Classifier[]>> => {
  let fetchedClassifierSets: Map<string, Classifier[]> = new Map();
  await fetchContractGroupedSubtypeClassifiers(languageCode).then((classifiers) => {
    fetchedClassifierSets = classifiers;
  });
  return fetchedClassifierSets;
};

const getMaareformMethodGroupedSubtypeClassifiers = async (
  languageCode: LanguageCode = "ET",
): Promise<Map<string, Classifier[]>> => {
  /*const storedClassifierSets = retrieveClassifierSets(
		languageCode
	);*/
  let fetchedClassifierSets: Map<string, Classifier[]> = new Map();
  await fetchMaarefomMethodGroupedSubtypeClassifiers(languageCode).then((classifiers) => {
    //storeClassifierSets(classifiers, languageCode);
    fetchedClassifierSets = classifiers;
  });
  return fetchedClassifierSets;
};

const retrieveClassifierSets = (requestedTypes: ClassifierType[], languageCode: LanguageCode): ClassifierSet[] => {
  const matchingClassifierSets: ClassifierSet[] = [];
  const storedClassifierSets = getStoredClassifierSets(languageCode);
  if (storedClassifierSets) {
    requestedTypes.forEach((type) => {
      const matchingClassifier = storedClassifierSets.find((set) => {
        return type === set.type;
      });
      if (matchingClassifier) {
        matchingClassifierSets.push(matchingClassifier);
      }
    });
  }
  return matchingClassifierSets;
};

const storeClassifierSets = (classifierSets: ClassifierSet[], languageCode: LanguageCode) => {
  const { storedCollections, storedClassifierSets } = getOrCreateStoredClassifierSets(languageCode);

  classifierSets.forEach((set) => {
    storedClassifierSets?.push(set);
  });

  storeClassifiers(storedCollections);
};

const fetchClassifierSets = async (types: ClassifierRequestType[], languageCode: LanguageCode) => {
  const classifierRequestBody: ClassifierRequestBody = {
    langCode: languageCode,
    requestedFields: types,
  };
  const response = await post<ClassifierRequestBody, ClassifierResponseBody>({
    endpoint: "classifiers",
    requestBody: classifierRequestBody,
  });
  const { body } = response;

  delete body.langCode;

  const classifierSets: ClassifierSet[] = [];
  const classifierTypes = Object.getOwnPropertyNames(body) as ClassifierType[];
  classifierTypes.forEach((classifierType) => {
    const classifierSet: ClassifierSet = {
      type: classifierType,
      classifiers: [],
    };

    body[classifierType]?.forEach((classifier) => {
      classifierSet.classifiers.push({
        label: classifier.label,
        value: classifier.code,
      });
    });
    classifierSets.push(classifierSet);
  });
  return classifierSets;
};

const fetchGroupedSubtypeClassifiers = async (start: string, languageCode: LanguageCode) => {
  const { body: response } = await get<GroupedSubtypeClassifierResponseBody>({
    endPoint: "groupedProceedingTypesClassifiers",
    queryParams: [
      {
        name: "alusta",
        value: start,
      },
      {
        name: "langCode",
        value: languageCode,
      },
    ],
  });
  const mappedResp: Map<string, Classifier[]> = new Map();
  const theseKeys = Object.getOwnPropertyNames(response) as string[];

  theseKeys.forEach((k) => {
    const v1 = response[k];
    if (v1) {
      const v2: Classifier[] = [];
      v1.forEach((classifier) => {
        v2.push({
          label: classifier.label,
          value: classifier.code,
        });
        mappedResp.set(k, v2);
      });
    }
  });

  return mappedResp;
};

const fetchContractGroupedSubtypeClassifiers = async (languageCode: LanguageCode) => {
  const { body: response } = await get<GroupedSubtypeClassifierResponseBody>({
    endPoint: "groupedContractProceedingTypesClassifiers",
    queryParams: [
      {
        name: "langCode",
        value: languageCode,
      },
    ],
  });
  const mappedResp: Map<string, Classifier[]> = new Map();
  const theseKeys = Object.getOwnPropertyNames(response) as string[];

  theseKeys.forEach((k) => {
    const v1 = response[k];
    if (v1) {
      const v2: Classifier[] = [];
      v1.forEach((classifier) => {
        v2.push({
          label: classifier.label,
          value: classifier.code,
        });
        mappedResp.set(k, v2);
      });
    }
  });

  return mappedResp;
};

const fetchMaarefomMethodGroupedSubtypeClassifiers = async (languageCode: LanguageCode) => {
  const { body: response } = await get<GroupedSubtypeClassifierResponseBody>({
    endPoint: "groupedMaareformMethodToProceedingSubtypesClassifiers",
    queryParams: [
      {
        name: "langCode",
        value: languageCode,
      },
    ],
  });
  const mappedResp: Map<string, Classifier[]> = new Map();
  const theseKeys = Object.getOwnPropertyNames(response) as string[];

  theseKeys.forEach((k) => {
    const v1 = response[k];
    if (v1) {
      const v2: Classifier[] = [];
      v1.forEach((classifier) => {
        v2.push({
          label: classifier.label,
          value: classifier.code,
        });
        mappedResp.set(k, v2);
      });
    }
  });

  return mappedResp;
};

const mapClassifiersValuesToLabels = async (
  classifiersValues: {
    type: ClassifierType;
    values: string[] | string[][];
  }[],
  languageCode: LanguageCode = "ET",
): Promise<{ [key in ClassifierType]?: string[] }> => {
  const sets = await getClassifiers(
    classifiersValues.map((classifier) => classifier.type),
    languageCode,
  );
  const labelsObject: ViewClassifierLabelsObject = {};

  classifiersValues.forEach((classifierValues) => {
    const set = sets.find((set) => {
      return set.type === classifierValues.type;
    })!;
    labelsObject[set.type] = [];
    classifierValues.values.forEach((value: string[] | string) => {
      if (!Array.isArray(value)) {
        labelsObject[set.type]?.push(
          set.classifiers.find((classifer) => {
            return value === classifer.value;
          })?.label || "",
        );
      } else {
        //for separating multiple classifier values in one string, created for type string[][]
        labelsObject[set.type]?.push(
          value
            .map((innerValue) => set.classifiers.find((classifer) => innerValue === classifer.value)?.label)
            .join("; "),
        );
      }
    });
  });

  return labelsObject;
};

export interface LocalizedClassifierCollection {
  languageCode: LanguageCode;
  classifierSets: ClassifierSet[];
}

interface ClassifierSet {
  type: ClassifierType;
  classifiers: Classifier[];
}

export interface Classifier {
  value: string;
  label: string;
}

export interface NameAndId {
  id: number;
  nimi: string;
}

export const toClassifier = (label: string | undefined, value: string | undefined): Classifier | undefined => {
  if (label != undefined && value != undefined) {
    return { label, value };
  } else {
    return undefined;
  }
};

export interface ClassifierWithoutValue extends Omit<Classifier, "value"> {}

export type ViewClassifierObject = {
  [key in ClassifierType]?: Classifier[];
};

export type ViewClassifierLabelsObject = {
  [key in ClassifierType]?: string[];
};

export type ClassifierType = Exclude<keyof ClassifierResponseBody, "langCode">;

const ClassifierTypeMap = new Map<ClassifierType, ClassifierRequestType>([
  ["automaagika", "AUTOMAAGIKA"],
  ["estateSearchTypes", "ESTATE_SEARCH_TYPES"],
  ["objectTypes", "OBJECT_TYPES"],
  ["estatePurposeTypes", "ESTATE_PURPOSE_TYPES"],
  ["estatePurposeTypesValitsemised", "ESTATE_PURPOSE_TYPES_VALITSEMISED"],
  ["heitmedTypes", "HEITMED_TYPES"],
  ["investeeringuLiikTypes", "INVESTEERINGU_LIIK_TYPES"],
  ["ipOtsusTypes", "IP_OTSUS_TYPES"],
  ["ipOtsustajaTypes", "IP_OTSUSTAJA_TYPES"],
  ["ipStatusTypes", "IP_STATUS_TYPES"],
  ["juhtfunktsioonTypes", "JUHTFUNKTSIOON_TYPES"],
  ["kaibemaksumaaradTypes", "KAIBEMAKSUMAARAD_TYPES"],
  ["kvyActionTypes", "KVY_ACTION_TYPES"],
  ["kvyEnergiatohususTypes", "KVY_ENERGIATOHUSUS"],
  ["kvyKasutusnaitajaTypes", "KVY_KASUTUSNAITAJAD"],
  ["kvyPinnaTyyp", "KVY_PINNA_TYYP"],
  ["kvyPlaneerimisetapidTypes", "KVY_PLANEERIMISETAPID"],
  ["kvyPrioriteetsusTypes", "KVY_PRIORITEETSUS"],
  ["kvySeisukordNrTypes", "KVY_SEISUKORD_NR"],
  ["kvyStatusTypes", "KVY_STATUS_TYPES"],
  ["kvyLopetamisePohjus", "KVY_LOPETAMISE_POHJUS"],
  ["kvyVajalikuKuluMeetodTypes", "KVY_KULU_MEETOD"],
  ["menetlusStateTypes", "MENETLUS_STATE_TYPES"],
  ["menetlusType", "MENETLUS_TYPE"],
  ["menetlusTypeStart", "MENETLUS_TYPE_START"],
  ["menetlusTypeSubtype", "MENETLUS_TYPE_SUBTYPE"],
  ["menetlusVoorandamisTypes", "MENETLUS_VOORANDAMIS_TYPES"],
  ["menetlusVoorandamisTypesVoorand", "MENETLUS_VOORANDAMIS_TYPES_VOORAND"],
  ["menetlusVoorandamisTypesMaareform", "MENETLUS_VOORANDAMIS_TYPES_MAAREFORM"],
  ["menetlusDocumentTypes", "MENETLUS_DOCUMENT_TYPES"],
  ["menetlusDocumentTypesOther", "MENETLUS_DOCUMENT_TYPES_OTHER"],
  ["maareformDocumentTypes", "MAAREFORM_DOCUMENT_TYPES"],
  ["menetlusLopetamisePohjus", "MENETLUS_LOPETAMISE_POHJUS"],
  ["lopetamisePohjusTypesOther", "MENETLUS_LOPETAMISE_POHJUS_OTHER"],
  ["lopetamisePohjusTypesValdus", "MENETLUS_LOPETAMISE_POHJUS_VALDUS"],
  ["menetlusOiguslikAlus", "MENETLUS_OIGUSLIK_ALUS"],
  ["menetlusTeateTypes", "MENETLUS_TEATE_TYPES"],
  ["menetlusDetailNoudedTaidetud", "MENETLUS_DETAIL_NOUDED_TAIDETUD"],
  ["menetlusDetailTegevusluba", "MENETLUS_DETAIL_TEGEVUSLUBA"],
  ["menetluseAlamseisundTypes", "MENETLUSE_ALAMSEISUND_TYPE"],
  ["menetluseEesmarkTypes", "MENETLUSE_EESMARK_TYPE"],
  ["menetluseOsapooledTyyp", "MENETLUSE_OSAPOOLED_TYYP"],
  ["menetlusobjDetailHinnanguKoostaja", "MENETLUSOBJ_DETAIL_HINNANGU_KOOSTAJA"],
  ["menetlusobjDetailLoaVajadus", "MENETLUSOBJ_DETAIL_LOA_VAJADUS"],
  ["menetlusobjDetailMenetlusePohjus", "MENETLUSOBJ_DETAIL_MENETLUSE_POHJUS"],
  ["hankedKorraldaja", "HANKED_KORRALDAJA"],
  ["hankedLiik", "HANKED_LIIK"],
  ["hankedTulemus", "HANKED_TULEMUS"],
  ["menetlusaladUlatus", "MENETLUSALAD_ULATUS"],
  ["hinnadKasutusPeriood", "HINNAD_KASUTUS_PERIOOD"],
  ["menetlusOsapooledPoolTypes", "MENETLUS_OSAPOOLED_POOL_TYPES"],
  ["lepingTahtaegLiik", "LEPING_TAHTAEG_LIIK"],
  ["manusTypes", "MANUS_TYPES"],
  ["notifyType", "NOTIFY_TYPE"],
  ["notifySender", "NOTIFY_SENDER"],
  ["oksjonidTulemus", "OKSJONID_TULEMUS"],
  ["organOiguslikVorm", "ORGAN_OIGUSLIK_VORM"],
  ["lepingType", "LEPING_TYPE"],
  ["lepingTypeSubtype", "LEPING_TYPE_SUBTYPE"],
  ["lepingMainType", "LEPING_MAIN_TYPE"],
  ["lepingSeisundTypes", "LEPING_SEISUND_TYPES"],
  ["lepingKasutusviis", "LEPING_KASUTUSVIIS"],
  ["lisainfoTypes", "LISAINFO_TYPE"],
  ["lisainfoStatusTypes", "LISAINFO_STATUS_TYPE"],
  ["maaPohiotstarveType", "MAA_POHIOTSTARVE_TYPE"],
  ["manuaalAnalyysTyyp", "MANUAAL_ANALYYS_TYYP"],
  ["analyysTyyp", "ANALYYS_TYYP"],
  ["planeeringTyyp", "PLANEERING_TYYP"],
  ["huviTyyp", "HUVI_TYPE"],
  ["hooneKasutusotstarveTypes", "HOONE_KASUTOTSTARVE_TYPE"],
  ["rajatisKasutusotstarveTypes", "RAJATIS_KASUTOTSTARVE_TYPE"],
  ["taotluseTyyp", "TAOTLUSE_TYYP"],
  ["lepinguOsapooledTyyp", "LEPINGU_OSAPOOLED_TYYP"],
  ["lepinguOsapooledPoolTypes", "LEPINGU_OSAPOOLED_POOL_TYPES"],
  ["contractPaymentType", "CONTRACT_PAYMENT_TYPE"],
  ["yyripindSihtotstarveType", "YYRIPIND_SIHTOTSTARVE_TYPE"],
  ["lepingDocumentTypes", "LEPING_DOCUMENT_TYPES"],
  ["contractMaintenanceWorkType", "CONTRACT_MAINTENENCE_WORK_TYPE"],
  ["kasutustasuAlusType", "KASUTUSTASU_ALUS"],
  ["userRoleTypes", "USER_ROLE_TYPES"],
  ["viimaneToimingType", "VIIMANE_TOIMING_TYPE"],
  ["kaimasolevToimingTypes", "KAIMASOLEV_TOIMING_TYPES"],
  ["omandamiseOtsustajaTypes", "OMANDAMISE_OTSUSTAJA_TYPES"],
  ["ehitiseLiikTypes", "EHITISE_LIIK_TYPES"],
  ["menetlusDetailKasutusviis", "MENETLUS_DETAIL_KASUTUSVIIS"],
  ["organisationTypes", "ORGANISATION_TYPES"],
  ["organisationRegTypes", "ORGANISATION_REG_TYPES"],
  ["piiranguLiikTypes", "PIIRANGU_LIIK_TYPE"],
  ["sihtotstarveTypes", "SIHTOTSTARVE_TYPE"],
  ["kestvusperioodiYhikTypes", "KESTVUSPERIOODI_UHIK_TYPE"],
  ["pindalaYhikTypes", "YHIK_TYPE"],
  ["hooneKasutusotstarveTypes", "HOONE_KASUTOTSTARVE_TYPE"],
  ["valitsemiseEesmarkPohjusTypes", "EESMARGI_POHJUS_TYPE"],
  ["loendiTypes", "LOENDI_TYPES"],
  ["taotletavOtsusTypes", "TAOTLETAV_OTSUS_TYPES"],
  ["varaLiikTypes", "VARA_LIIK_TYPES"],
  ["kvyFinantseerimisallikasTypes", "KVY_FINANTSEERIMISALLIKAS"],
  ["lepingOiguslikAlus", "LEPING_OIGUSLIK_ALUS"],
  ["elusolekTypes", "ELUSOLEK_TYPES"],
  ["lihtKodakondsusTypes", "LIHT_KODAKONDSUS_TYPES"],
  ["maksegraafikuStaatusTypes", "MAKSEGRAAFIKU_STAATUS_TYPES"],
  ["tasumiseSagedus", "TASUMISE_SAGEDUS"],
  ["makseStaatusTypes", "MAKSE_STAATUS_TYPES"],
]);

export type LanguageCode = "ET";

export const getBooleanClassifiersOLD = (t: any): Classifier[] => {
  return [
    {
      label: t("common.yes"),
      value: "true",
    },
    {
      label: t("common.no"),
      value: "false",
    },
  ];
};

export const getBooleanClassifiers = (
  t: any,
  customLabels?: [string, string, string],
): [Classifier, Classifier, ClassifierWithoutValue] => {
  const [trueLabel, falseLabel, undefinedLabel] = customLabels || [];
  return [
    {
      label: trueLabel || t("common.yes"),
      value: "true",
    },
    {
      label: falseLabel || t("common.no"),
      value: "false",
    },
    {
      label: undefinedLabel || t("common.undefined"),
    },
  ];
};

export const getAssetTypeClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("reports.assetType.land"),
      value: AssetType.MAA,
    },
    {
      label: t("reports.assetType.buildings"),
      value: AssetType.HOONE,
    },
  ];
};

export const getAssetPartTypeClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("reports.assetType.land"),
      value: VaraOsisType.KATASTRIYKSUS,
    },
    {
      label: t("reports.assetType.buildings"),
      value: VaraOsisType.HOONE,
    },
    {
      label: t("reports.assetType.facilities"),
      value: VaraOsisType.RAJATIS,
    },
  ];
};

export const getContractAssetTypeClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("reports.assetType.land"),
      value: VaraOsisType.KATASTRIYKSUS,
    },
    {
      label: t("reports.assetType.buildings"),
      value: VaraOsisType.HOONE,
    },
  ];
};

export const getProceedingAssetTypeClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("reports.assetType.land"),
      value: VaraOsisType.KATASTRIYKSUS,
    },
    {
      label: t("reports.assetType.buildings"),
      value: VaraOsisType.HOONE,
    },
  ];
};

export const getAuthorizedBodyUnspecifiedClassifier = (t: any): Classifier => {
  return {
    label: t("common.notSelected"),
    value: Unspecified.UNSPECIFIED,
  };
};

export const getAddressSearchTypeClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("addressComponent.ADS"),
      value: AddressSearchType.ADS,
    },
    {
      label: t("addressComponent.freetext"),
      value: AddressSearchType.VABATEKST,
    },
  ];
};

export const getRealEstateComplexAddressSearchTypeClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("addressComponent.ADSOID"),
      value: RealEstateComplexAddressSearchType.ADSOID,
    },
    {
      label: t("addressComponent.ADS"),
      value: RealEstateComplexAddressSearchType.ADS,
    },
    {
      label: t("addressComponent.foreign"),
      value: RealEstateComplexAddressSearchType.FOREIGN,
    },
  ];
};

export const getProcessingStatusClassifiers = (t: any): Classifier[] => {
  return [
    {
      label: t("processingStatus.processed"),
      value: ProcessingStatus.PROCESSED,
    },
    {
      label: t("processingStatus.unprocessed"),
      value: ProcessingStatus.UNPROCESSED,
    },
    {
      label: t("processingStatus.faulty"),
      value: ProcessingStatus.FAULTY,
    },
  ];
};

export {
  getClassifiers,
  mapClassifiersValuesToLabels,
  getGroupedSubtypeClassifiers,
  getContractGroupedSubtypeClassifiers,
  getMaareformMethodGroupedSubtypeClassifiers,
};
